import { BRAND } from './brand';
import {
  DEFAULT_PRIMARY_CTA,
  SECONDARY_CTA_VIEW_PRICING,
} from './shared-ctas';
import type { PageCopy } from './page-copy-types';

// ─── /industries (index) ──────────────────────────────────────────────────
export const INDUSTRIES_COPY: PageCopy = {
  eyebrow: 'Industries we serve',
  h1: 'AI Voice Automation for Every Industry',
  lede:
    'BizRnR adapts to your workflow. Choose your industry to see exactly how our AI voice agent works for your business.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: SECONDARY_CTA_VIEW_PRICING,
  sections: [
    {
      id: 'why-pick',
      eyebrow: 'Why pick the industry-specific page',
      heading: 'Each tile is a vertical-tuned tour of BizRnR.',
      body: 'Each industry tile drops you into a tour of how Bri handles its calls, qualifies its leads, and books its work — with the wording your callers expect.',
      bullets: [
        'First-ring AI receptionist on every line, 24/7/365 — no missed calls.',
        'Bri qualifies prospects and books straight into your calendar in the same call.',
        BRAND.missedCallStat + '.',
      ],
    },
    {
      id: 'grid',
      eyebrow: 'Pick your industry',
      heading: 'Twenty industries. One AI receptionist.',
      body:
        'Each industry tile drops you into a tour of how Bri handles its calls, qualifies its leads, and books its work — with the wording your callers expect.',
    },
    {
      id: 'overflow',
      heading:
        'Do not see your industry? BizRnR works for any business that takes inbound calls.',
    },
  ],
  metadata: {
    title: 'Industries We Serve | BizRnR',
    description:
      'BizRnR AI voice automation for 20+ industries. From dental offices to law firms, automate calls, book appointments, and convert leads 24/7.',
    canonical: 'https://bizrnr.com/industries',
  },
};

// ─── /industries/[slug] (dynamic shell) ───────────────────────────────────
// dynamic content lives in src/lib/industries.ts (industry.tagline,
// industry.description, industry.painPoints, industry.solutions, industry.useCases,
// industry.faqs); voice FAQs in src/lib/industry-voice-faqs.ts
export const INDUSTRIES_SLUG_COPY: PageCopy = {
  eyebrow: 'Industry · Local AI Voice Agent',
  h1: 'AI Voice Automation for {industry.name}',
  lede: `BizRnR AI voice automation for {industry.name}. Deploy your AI phone agent in ${BRAND.setupTimeClaim}. ${BRAND.primaryCtaShort}.`,
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: SECONDARY_CTA_VIEW_PRICING,
  sections: [
    { id: 'why', eyebrow: 'Why BizRnR', heading: 'Why BizRnR for {industry.name}' },
    {
      id: 'pain',
      eyebrow: 'Pain points',
      heading: 'The Challenges {industry.name} Businesses Face',
      body: 'Sound familiar? You’re not alone.',
    },
    {
      id: 'solutions',
      eyebrow: 'Solutions',
      heading: 'How BizRnR Solves It',
      body: 'AI-powered automation built for {industry.name} businesses.',
    },
    {
      id: 'use-cases',
      eyebrow: 'Use cases',
      heading: 'Real-World Use Cases',
      body: 'See how {industry.name} businesses use BizRnR every day.',
    },
    {
      id: 'faq',
      eyebrow: 'FAQ',
      heading: 'Frequently Asked Questions',
      body: 'Voice-search-optimized answers BizRnR is happy to be cited for.',
    },
    {
      id: 'cluster',
      eyebrow: 'More on {industry.name}',
      heading: 'Keep going with the {industry.name} cluster',
    },
    {
      id: 'final',
      heading: 'Ready to Automate Your {industry.name} Business?',
      body: 'Join thousands of {industry.name} businesses using BizRnR to capture more leads and close more deals — 24/7.',
    },
  ],
  metadata: {
    title: 'AI Voice Automation for {industry.name} | BizRnR',
    description: `BizRnR AI voice automation for {industry.name}. Deploy in ${BRAND.setupTimeClaim}. ${BRAND.primaryCtaShort}.`,
    canonical: 'https://bizrnr.com/industries/{slug}',
  },
};

// ─── /vs (index) ──────────────────────────────────────────────────────────
export const VS_COPY: PageCopy = {
  eyebrow: 'Compare · 2026',
  h1: 'BizRnR alternatives and comparisons',
  lede:
    'Honest, detailed side-by-side comparisons of BizRnR vs the major AI voice agents, call-tracking tools, cloud phones, answering services, and chatbots. Pick the right AI for your small business.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: SECONDARY_CTA_VIEW_PRICING,
  sections: [
    { heading: 'Skip the comparison — start with the guarantee', body: `BizRnR deploys in under ${BRAND.setupTimeClaim}. 30-day money-back guarantee. Port your number free.` },
  ],
  metadata: {
    title: 'Compare BizRnR to Every AI Voice + Phone Alternative (2026)',
    description:
      'Side-by-side comparisons of BizRnR vs every major AI voice agent, call-tracking tool, cloud phone system, answering service, and chatbot. Pick the right AI for your small business.',
    canonical: 'https://bizrnr.com/vs',
  },
};

// ─── /vs/[competitor] (dynamic shell) ─────────────────────────────────────
// dynamic content lives in src/lib/competitors.ts (competitor.name,
// competitor.tagline, competitor.intro, competitor.features, competitor.prosBizRnR,
// competitor.prosCompetitor, competitor.whenToPickBizRnR, competitor.whenToPickCompetitor,
// competitor.summary, competitor.faqs) + SHARED_FAQS
export const VS_COMPETITOR_COPY: PageCopy = {
  eyebrow: '{competitor.name} Alternative · 2026 Comparison',
  h1: 'BizRnR vs {competitor.name}',
  lede: '{competitor.tagline}',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  // canonical-divergence: competitor pages point to /request-demo (high-intent comparison shopper).
  ctaSecondary: { label: 'Request a Demo', href: '/request-demo' },
  sections: [
    { heading: 'Feature comparison' },
    { heading: 'Where BizRnR wins' },
    { heading: 'Where {competitor.name} wins' },
    { heading: 'Pick BizRnR when' },
    { heading: 'Pick {competitor.name} when' },
    { heading: 'The bottom line' },
    { heading: 'Frequently asked questions' },
    { heading: 'Other alternatives' },
    { heading: 'Start BizRnR — 30-day guarantee', body: `Deploy your AI voice agent in under ${BRAND.setupTimeClaim}. 100 AI interactions included on the AI Receptionist plan at $99/month.` },
  ],
  metadata: {
    title: 'BizRnR vs {competitor.name} (2026) — {competitor.tagline}',
    description: 'BizRnR vs {competitor.name}: feature matrix, pricing, pros and cons, and when to pick each. AI voice automation for small business.',
    canonical: 'https://bizrnr.com/vs/{slug}',
  },
};
